import Link from "@tiptap/extension-link";
import { generateHTML, generateJSON, type JSONContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { find } from "linkifyjs";

/**
 * Schema for rich contact notes: what the shared `TiptapEditor` produces
 * (paragraphs, marks, lists, blockquotes, code, links) minus headings, so a
 * note stays a note. Shared by the composer and the renderer so what you
 * can type is exactly what displays.
 */
export const noteExtensions = [
  StarterKit.configure({
    heading: false,
    link: false,
  }),
  Link.configure({
    openOnClick: false,
    autolink: true,
    HTMLAttributes: {
      class: "text-primary dark:text-brand-accent underline",
      rel: "noopener noreferrer",
      target: "_blank",
    },
  }),
];

/**
 * Notes written before the rich composer are plain text. Anything the
 * composer saves starts with a block element.
 */
export function isRichNote(body: string): boolean {
  return /^\s*<(p|ul|ol|blockquote)[\s>]/.test(body);
}

/**
 * Editor-ready HTML for an existing comment. Rich notes load as they are;
 * plain-text comments become one paragraph per line so their breaks survive
 * the round trip through the editor.
 */
export function noteToEditorHtml(body: string): string {
  if (isRichNote(body)) return body;
  return body
    .split(/\r?\n/)
    .map(
      (line) => `<p>${line.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")}</p>`,
    )
    .join("");
}

/**
 * Wraps bare URLs in text nodes with a link mark. The editor autolinks only
 * when whitespace follows a URL, so a note posted straight after typing an
 * address would otherwise keep it as plain text. Text already inside a link
 * or inline code is left alone.
 */
function linkifyNode(node: JSONContent): JSONContent[] {
  if (node.type === "text" && node.text) {
    if (node.marks?.some((mark) => mark.type === "link" || mark.type === "code")) return [node];
    const links = find(node.text, "url");
    if (!links.length) return [node];
    const parts: JSONContent[] = [];
    let cursor = 0;
    for (const link of links) {
      if (link.start > cursor) parts.push({ ...node, text: node.text.slice(cursor, link.start) });
      parts.push({
        ...node,
        text: link.value,
        marks: [...(node.marks ?? []), { type: "link", attrs: { href: link.href } }],
      });
      cursor = link.end;
    }
    if (cursor < node.text.length) parts.push({ ...node, text: node.text.slice(cursor) });
    return parts;
  }
  if (node.content) return [{ ...node, content: node.content.flatMap(linkifyNode) }];
  return [node];
}

/**
 * Round-trips stored HTML through the note schema, so only nodes, marks,
 * and attributes the schema knows survive. Anything else (scripts, event
 * handlers, unknown tags) is dropped before the markup is rendered.
 */
export function renderNoteHtml(body: string): string {
  if (typeof window === "undefined") return "";
  try {
    const [doc] = linkifyNode(generateJSON(body, noteExtensions));
    return generateHTML(doc, noteExtensions);
  } catch {
    return "";
  }
}

/**
 * Drops the empty blocks a keyboard-driven composer leaves behind: the
 * blank list item after the last bullet and any trailing blank paragraphs.
 */
export function trimNoteHtml(html: string): string {
  let out = html;
  let prev = "";
  while (out !== prev) {
    prev = out;
    out = out
      .replace(/<li>\s*<p>\s*<\/p>\s*<\/li>/g, "")
      .replace(/<(ul|ol)>\s*<\/\1>\s*$/g, "")
      .replace(/<p>\s*<\/p>\s*$/g, "")
      .trim();
  }
  return out;
}

/** Empty when the editor holds only whitespace or an empty paragraph. */
export function isNoteEmpty(html: string): boolean {
  return !html
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .trim();
}
